import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import { ACTIVE_CHAINS } from '@/config/chains'
import { getContractAddress } from '@/config/contracts'
import { operatorRolesAbi } from '@/abi/operatorRolesAbi'

// Reads OperatorRoles membership (`hasAnyConsoleRole`) for `account` on every
// active chain that has an OperatorRoles address configured. This is the
// console sign-in eligibility gate — it replaces the old
// `useOperatorRoleAcrossChains` PlayerRegistry.OPERATOR_ROLE probe so that
// console access is decoupled from on-chain write authority.
export function useConsoleRoleAcrossChains(account?: `0x${string}`) {
    const contracts = useMemo(() => {
        if (!account) return []
        return ACTIVE_CHAINS.flatMap((c) => {
            const addr = getContractAddress(c.id, 'operatorRoles')
            if (!addr) return []
            return [
                {
                    address: addr,
                    abi: operatorRolesAbi,
                    functionName: 'hasAnyConsoleRole' as const,
                    args: [account] as const,
                    chainId: c.id,
                },
            ]
        })
    }, [account])

    const { data, isLoading, isError } = useReadContracts({
        contracts,
        allowFailure: true,
        query: { enabled: contracts.length > 0 },
    })

    const rolesByChain = useMemo<Record<number, boolean>>(() => {
        const out: Record<number, boolean> = {}
        contracts.forEach((c, i) => {
            const result = data?.[i]
            out[c.chainId] = result?.status === 'success' && result.result === true
        })
        return out
    }, [contracts, data])

    const eligibleChainIds = useMemo(
        () => contracts.filter((c) => rolesByChain[c.chainId]).map((c) => c.chainId),
        [contracts, rolesByChain],
    )

    return { rolesByChain, eligibleChainIds, isLoading, isError }
}
